package us.laantech.admin.menu.clients.products

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.Button
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.ParentCompany
import us.laantech.common.ui.DataTableView
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.api.KtorClient
import us.laantech.routes.users.UserDTO

data class ManageRetailProductsUiState(
    val users: List<UserDTO> = listOf(),
    val selectedUser: UserDTO? = null,

    val clients: List<ParentCompany> = listOf(),
    val selectedClient: ParentCompany? = null,
    // Branches are a separate collection, fetched for the selected client.
    val branches: List<CompanyBranch> = listOf(),
    val selectedBranch: CompanyBranch? = null,

    val showConfigurator: Boolean = false
)

@Composable
fun ManageClientProductsView(
    viewModel: ManageRetailProductsViewModel = viewModel(
        factory = viewModelFactory {
            initializer { ManageRetailProductsViewModel() }
        }
    )
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        viewModel.loadUsers()
        viewModel.loadClients()
    }

    if (uiState.showConfigurator) {
        RetailProductsManagerView(
            accountSub = uiState.selectedUser?.sub,
            companyBranch = uiState.selectedBranch!!,
            onDismiss = { viewModel.toggleShowConfigurator(uiState.selectedBranch) }
        )
    } else {
        FilterAndSelect(viewModel)
    }
}

@Composable
fun FilterAndSelect(viewModel: ManageRetailProductsViewModel) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    Column {
        Text("Retail Products Management")

        // Filters
        Row(Modifier.fillMaxWidth()) {
            // Select account
            DropdownMenu(
                label = "Select Account",
                items = uiState.users,
                selectedItem = uiState.selectedUser,
                itemLabel = { it.username.orEmpty() },
                onItemSelected = { selected ->
                    viewModel.onUserSelected(selected)
                },
                modifier = Modifier.weight(1f),
                noneLabel = "None"
            )

            // Select client
            DropdownMenu(
                label = "Select Client",
                items = uiState.clients,
                selectedItem = uiState.selectedClient,
                itemLabel = { it.companyInfo?.companyName.orEmpty() },
                onItemSelected = { selected ->
                    viewModel.onClientSelected(selected)
                },
                modifier = Modifier.weight(2f),
                noneLabel = "None"
            )
        }


        // Company stores
        DataTableView(
            modifier = Modifier.fillMaxSize(),
            rows = uiState.branches
        ) {
            column("Branch") { Text(it.branchName.orEmpty(), modifier = Modifier.weight(1f)) }
            column("Address") { Text(it.address.toString(), modifier = Modifier.weight(2f)) }
            column("Configure") {
                Button(
                    { viewModel.toggleShowConfigurator(it) },
                    modifier = Modifier.weight(1f),
                    content = { Text("Manage Products") }
                )
            }

        }
    }

}



class ManageRetailProductsViewModel: ViewModel() {

    val _uiState = MutableStateFlow(ManageRetailProductsUiState())
    val uiState: StateFlow<ManageRetailProductsUiState> = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client

    suspend fun loadUsers() {
        val response = ktClient.get("/admin/accounts/list")
        val users = response.body<List<UserDTO>>()
        _uiState.update { it.copy(users = users) }
    }

    fun toggleShowConfigurator(selectedBranch: CompanyBranch?) {
        _uiState.update { it.copy(
            selectedBranch = selectedBranch,
            showConfigurator = !it.showConfigurator
        ) }
    }

    fun onUserSelected(selected: UserDTO?) {
        viewModelScope.launch {
            _uiState.update { it.copy(selectedUser = selected) }
            loadClients()
        }
    }

    suspend fun loadClients() {
        val response = ktClient.get("/admin/internal-clients/list") {
            _uiState.value.selectedUser?.let {
                parameter("accountSub", it.sub)
            }
        }
        val clients = response.body<List<ParentCompany>>()
        _uiState.update { it.copy(clients = clients) }
    }

    fun onClientSelected(selected: ParentCompany? = null) {
        viewModelScope.launch {
            _uiState.update { it.copy(selectedClient = selected, branches = emptyList()) }
            loadBranches(selected)
        }
    }

    private suspend fun loadBranches(client: ParentCompany?) {
        client ?: return
        val response = ktClient.get("/admin/internal-clients/list/branches") {
            parameter("accountSub", client.accountSub)
            client.uuid?.let { parameter("parentCompanyUuid", it) }
        }
        val branches = response.body<List<CompanyBranch>>()
        _uiState.update { it.copy(branches = branches) }
    }
}